/* src/components/customGame/setup/panels/stimuli/AudioStimulusPanel.js */
import React from 'react';
import PropTypes from 'prop-types';

// Helper for consistent sub-section styling within a panel
const SubSettingsSection = ({ title, children, helpText = "" }) => (
    <div className="mb-4 pt-3"> {/* Removed border/shadow to be less visually heavy than main SettingsSection */}
        <label className="block text-sm font-semibold text-gray-700 mb-1">{title}</label>
        {helpText && <p className="text-xs text-gray-500 mb-2">{helpText}</p>}
        <div className="space-y-3">
            {children}
        </div>
    </div>
);

SubSettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
};


const AudioStimulusPanel = ({ config, onConfigChange, pathPrefix, globalStimulusConfig }) => {

    // Fallback for config if it's undefined (e.g., if defaults weren't set correctly upstream)
    const currentConfig = config || {};

    const handleChange = (key, value) => {
        onConfigChange(`${pathPrefix}.${key}`, value);
    };

    return (
        <div className="space-y-4">
            {/* Section for Audio Stimulus Set */}
            <SubSettingsSection title="Audio Stimulus Set" helpText="Choose the type and source of sounds.">
                <div>
                    <label htmlFor={`${pathPrefix}-SetType`} className="block text-xs font-medium text-gray-600 mb-1">
                        Stimulus Type
                    </label>
                    <select
                        id={`${pathPrefix}-SetType`}
                        name="SetType"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.SetType || 'Letters'}
                        onChange={(e) => handleChange('SetType', e.target.value)}
                    >
                        <option value="Letters">Letters</option>
                        <option value="Words">Words (Thematic)</option>
                        {/* <option value="Tones">Tones (Non-Verbal)</option> */}
                        {/* <option value="Custom Upload">Custom Upload (Advanced)</option> */}
                    </select>
                </div>

                {currentConfig.SetType === 'Letters' && (
                    <div>
                        <label htmlFor={`${pathPrefix}-LetterSet`} className="block text-xs font-medium text-gray-600 mb-1">
                            Letter Set
                        </label>
                        <select
                            id={`${pathPrefix}-LetterSet`}
                            name="LetterSet"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            value={currentConfig.LetterSet || 'Standard Consonants (Jaeggi)'}
                            onChange={(e) => handleChange('LetterSet', e.target.value)}
                        >
                            <option value="Standard Consonants (Jaeggi)">Standard Consonants (Jaeggi: C,G,H,K,P,Q,T,W)</option>
                            <option value="Full Alphabet">Full Alphabet</option>
                            {/* <option value="Custom List">Custom List (Specify Below)</option> */}
                        </select>
                        {/* TODO: Input for Custom Letter List if selected */}
                    </div>
                )}

                {currentConfig.SetType === 'Words' && (
                     <div>
                        <label htmlFor={`${pathPrefix}-WordList`} className="block text-xs font-medium text-gray-600 mb-1">
                            Word List Theme
                        </label>
                        <select
                            id={`${pathPrefix}-WordList`}
                            name="WordList"
                            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            value={currentConfig.WordList || 'Theme: Objects'}
                            onChange={(e) => handleChange('WordList', e.target.value)}
                        >
                            <option value="Theme: Objects">Theme: Common Objects</option>
                            <option value="Theme: Animals">Theme: Animals</option>
                            {/* <option value="Custom List">Custom Word List (Specify Below)</option> */}
                        </select>
                        {/* TODO: Input for Custom Word List if selected */}
                    </div>
                )}

                <div>
                    <label htmlFor={`${pathPrefix}-Source`} className="block text-xs font-medium text-gray-600 mb-1">
                        Audio Source
                    </label>
                    <select
                        id={`${pathPrefix}-Source`}
                        name="Source"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.Source || 'Pre-recorded Pack'}
                        onChange={(e) => handleChange('Source', e.target.value)}
                    >
                        <option value="Pre-recorded Pack">Pre-recorded Sound Pack</option>
                        <option value="TTS">Text-To-Speech (TTS)</option>
                    </select>
                </div>
                 {currentConfig.Source === 'TTS' && (
                    <div>
                        <label htmlFor={`${pathPrefix}-TTSVoice`} className="block text-xs font-medium text-gray-600 mb-1">
                            TTS Voice (if available)
                        </label>
                        <input
                            type="text" // This would ideally be a select populated by available voices
                            id={`${pathPrefix}-TTSVoice`}
                            name="TTSVoice"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={currentConfig.TTSVoice || 'System Default'}
                            onChange={(e) => handleChange('TTSVoice', e.target.value)}
                            placeholder="e.g., System Default, Google US English"
                        />
                        <p className="text-xs text-gray-500 mt-1">Voice availability depends on your browser/OS.</p>
                    </div>
                )}
            </SubSettingsSection>

            <SubSettingsSection title="Audio Properties" helpText="Adjust sound characteristics.">
                 <div>
                    <label htmlFor={`${pathPrefix}-Volume`} className="block text-xs font-medium text-gray-600 mb-1">
                        Relative Volume (0.1 to 1.0)
                    </label>
                    <input
                        type="number"
                        id={`${pathPrefix}-Volume`}
                        name="Volume"
                        className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={currentConfig.Volume !== undefined ? currentConfig.Volume : 1.0}
                        onChange={(e) => handleChange('Volume', parseFloat(e.target.value))}
                        min="0.1"
                        max="1.0"
                        step="0.05"
                    />
                </div>
                {/* Placeholder for Spatialization - can be simple slider or L/C/R buttons */}
                {/* <div>
                    <label htmlFor={`${pathPrefix}-Spatialization`} className="block text-xs font-medium text-gray-600 mb-1">
                        Spatialization (L/R Balance: -1 to 1)
                    </label>
                    <input
                        type="number"
                        id={`${pathPrefix}-Spatialization`}
                        name="Spatialization"
                        className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={currentConfig.Spatialization !== undefined ? currentConfig.Spatialization : 0.0}
                        onChange={(e) => handleChange('Spatialization', parseFloat(e.target.value))}
                        min="-1"
                        max="1"
                        step="0.1"
                    />
                </div> */}
            </SubSettingsSection>

            {/* Placeholder for Similarity Settings (Phase 2) */}
            {/* <SubSettingsSection title="Auditory Similarity Control" helpText="Configure how similar/distinct sounds are.">
                <div>
                    <label htmlFor={`${pathPrefix}-SimilarityMode`} className="block text-xs font-medium text-gray-600 mb-1">
                        Similarity Mode
                    </label>
                    <select
                        id={`${pathPrefix}-SimilarityMode`}
                        name="SimilarityMode"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.SimilarityMode || 'None (Distinct Items)'}
                        onChange={(e) => handleChange('SimilarityMode', e.target.value)}
                    >
                        <option value="None (Distinct Items)">None (Distinct Items)</option>
                        <option value="Phonetic">Phonetic Similarity (for Letters)</option>
                        <option value="Semantic">Semantic Similarity (for Words)</option>
                    </select>
                </div>
                 {(currentConfig.SimilarityMode === 'Phonetic' || currentConfig.SimilarityMode === 'Semantic') && (
                    <div>
                        <label htmlFor={`${pathPrefix}-SimilarityLevel`} className="block text-xs font-medium text-gray-600 mb-1">
                            Similarity Level (0.0 to 1.0)
                        </label>
                        <input
                            type="number"
                            id={`${pathPrefix}-SimilarityLevel`}
                            name="SimilarityLevel"
                            className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                            value={currentConfig.SimilarityLevel !== undefined ? currentConfig.SimilarityLevel : 0.5}
                            onChange={(e) => handleChange('SimilarityLevel', parseFloat(e.target.value))}
                            min="0"
                            max="1"
                            step="0.1"
                        />
                    </div>
                )}
            </SubSettingsSection> */}


            {/* Debug section to see the props (optional, for development) */}
            {/* <details className="mt-4 text-xs">
                <summary className="cursor-pointer text-gray-500">Show Audio Panel Debug Info</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded text-gray-700 overflow-auto">
                    Path Prefix: {pathPrefix}{"\n"}
                    Current Config: {JSON.stringify(currentConfig, null, 2)}{"\n"}
                    Global Stimulus Config: {JSON.stringify(globalStimulusConfig, null, 2)}
                </pre>
            </details> */}
        </div>
    );
};

AudioStimulusPanel.propTypes = {
    config: PropTypes.shape({
        SetType: PropTypes.string,
        LetterSet: PropTypes.string,
        WordList: PropTypes.string,
        SimilarityMode: PropTypes.string,
        SimilarityLevel: PropTypes.number,
        Source: PropTypes.string,
        TTSVoice: PropTypes.string,
        Spatialization: PropTypes.number,
        Volume: PropTypes.number,
    }), // Can be undefined if not yet initialized by controller
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Stimulus.Audio"
    globalStimulusConfig: PropTypes.object, // The entire Stimulus settings object from parent
};

export default AudioStimulusPanel;